// Maps between the medos `opportunities.v1.Opportunity` resource and the flat record
// the store and pages use. See OPPORTUNITIES_DATA_CONTRACTS.md for the envelope.

export const OPPORTUNITY_GVK = { group: 'opportunities', version: 'v1', kind: 'Opportunity' };

// What the client writes under `spec`; identity and timestamps belong to `meta` and `object`.
const SPEC_FIELDS = [
  'medicationName',
  'title',
  'description',
  'status',
  'owner',
  'scope',
  'concentrationUnit',
  'analysis',
  'proposedChanges',
  'estimatedImpacts',
  'notes',
];

// The spec fields an update may change; notes are only appended (appendNote).
export const UPDATABLE_FIELDS = SPEC_FIELDS.filter((field) => !['medicationName', 'notes'].includes(field));

const copy = (value) => JSON.parse(JSON.stringify(value));

const pick = (source, fields) =>
  Object.fromEntries(fields.filter((field) => source && field in source).map((field) => [field, source[field]]));

// medos takes the name from the request path, so the client chooses it.
export function newOpportunityName() {
  return `opp-${crypto.randomUUID()}`;
}

export function toOpportunityRecord(resource) {
  const object = resource?.object || {};
  return {
    notes: [],
    ...copy(pick(resource?.spec, SPEC_FIELDS)),
    id: resource?.meta?.name,
    createdBy: object.createdBy || null,
    createdAt: object.createdAt || null,
    updatedAt: object.lastModifiedAt || object.createdAt || null,
  };
}

// A new resource (object.version 0 means create). The creator owns it until reassigned.
export function newOpportunityResource(fields, actor) {
  return {
    ...OPPORTUNITY_GVK,
    object: { version: 0 },
    meta: { name: newOpportunityName() },
    spec: {
      ...copy(pick(fields, SPEC_FIELDS)),
      status: fields.status || 'saved',
      owner: actor || null,
      notes: [],
    },
  };
}

// medos has no PATCH: apply the patch to the fetched resource and store it back whole,
// keeping its object.version so a concurrent edit is rejected rather than overwritten.
export function patchOpportunityResource(resource, patch) {
  const next = copy(resource);
  UPDATABLE_FIELDS.forEach((field) => {
    if (field in patch) next.spec[field] = copy(patch[field] ?? null);
  });
  return next;
}

// Until the kind has a hook to stamp authors, the client records who wrote the note.
export function appendNote(resource, content, actor, at = new Date().toISOString()) {
  const next = copy(resource);
  next.spec.notes = [
    ...(next.spec.notes || []),
    { author: actor || null, content: String(content || ''), createdAt: at },
  ];
  return next;
}

export default {
  OPPORTUNITY_GVK,
  UPDATABLE_FIELDS,
  newOpportunityName,
  toOpportunityRecord,
  newOpportunityResource,
  patchOpportunityResource,
  appendNote,
};
